/**
 * hm-report-core.ts — części wspólne zestawień modułu „Handel i Magazyn".
 *
 * Sprzedaż i magazyn liczą się z innych kwot (sprzedaż: netto/VAT/brutto, magazyn: netto/wartość
 * i ILOŚĆ), ale arytmetyka pieniądza, rozdzielanie walut i budowanie koszyków są identyczne.
 * Trzymamy je TUTAJ, żeby druga kartoteka nie dostała kopii tych samych reguł — bo kopia rozjeżdża
 * się po cichu: poprawka zaokrąglenia w jednym miejscu nie dociera do drugiego.
 */

/** Klucz koszyka dla wiersza bez wartości w danym wymiarze. */
export const HM_REPORT_NONE_KEY = '__none__';

/** Powyżej tylu dokumentów w zakresie nie ciągniemy szczegółów — raport prosi o zawężenie. */
export const HM_REPORT_DETAILS_MAX = 1000;

/** Ile dokumentów front wysyła w jednym żądaniu o szczegóły. */
export const HM_REPORT_DETAILS_CHUNK = 50;

/** Tekst dziesiętny → grosze. Nieparsowalne traktujemy jak zero — raport to lustro cudzej bazy. */
export function toMinor(value: string | null | undefined): number {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
}

export function fromMinor(minor: number): string {
  return (minor / 100).toFixed(2);
}

/** Ilości mają trzy miejsca po przecinku i nie są pieniądzem — własna skala. */
export function toMilli(value: string | null | undefined): number {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 1000);
}

export function fromMilli(milli: number): string {
  return String(Math.round(milli) / 1000);
}

/** PLN pierwsze (dominująca waluta), reszta alfabetycznie — żeby kolejność nie skakała. */
export function compareCurrency(a: string, b: string): number {
  if (a === b) return 0;
  if (a === 'PLN') return -1;
  if (b === 'PLN') return 1;
  return a.localeCompare(b);
}

export type HmReportBucketIdentity = {
  key: string;
  /** Etykieta wzięta z danych (nazwa kontrahenta, seria, data). `null` = brak wartości. */
  label: string | null;
  /**
   * Token do przetłumaczenia na froncie — dla wymiarów, których etykieta NIE jest daną
   * (np. status dokumentu). Ustawiony ⇒ wygrywa z `label`.
   */
  labelKey: string | null;
};

export const HM_REPORT_NONE: HmReportBucketIdentity = {
  key: HM_REPORT_NONE_KEY,
  label: null,
  labelKey: null,
};

/**
 * Koszyk po identyfikatorze ze słownika. Wpis BEZ identyfikatora, ale z nazwą (nabywca
 * jednokrotny, kontrahent wpisany na dokumencie) dostaje własny koszyk po nazwie — inaczej
 * wszyscy tacy wpadliby do wspólnego „brak".
 */
export function bucketById(
  id: number | null,
  name: string | null,
  prefix: string,
): HmReportBucketIdentity {
  if (id != null) return { key: `${prefix}${id}`, label: name, labelKey: null };
  if (name && name.trim() !== '') {
    return { key: `${prefix}name:${name}`, label: name, labelKey: null };
  }
  return HM_REPORT_NONE;
}

export function bucketByText(
  value: string | null | undefined,
  label?: string | null,
): HmReportBucketIdentity {
  const raw = (value ?? '').trim();
  if (raw === '') return HM_REPORT_NONE;
  return { key: raw, label: label ?? raw, labelKey: null };
}

/**
 * Porządek koszyków: daty chronologicznie, reszta po etykiecie, a „brak" zawsze na końcu —
 * pusty koszyk pomiędzy nazwami wygląda jak dziura w danych.
 */
export function compareBuckets(
  a: { key: string; label: string | null; labelKey: string | null },
  b: { key: string; label: string | null; labelKey: string | null },
  chronological: boolean,
): number {
  if (a.key === HM_REPORT_NONE_KEY) return 1;
  if (b.key === HM_REPORT_NONE_KEY) return -1;
  if (chronological) return a.key.localeCompare(b.key);
  const al = a.label ?? a.labelKey ?? a.key;
  const bl = b.label ?? b.labelKey ?? b.key;
  return al.localeCompare(bl, 'pl');
}
